import React, { useRef, useState, useEffect } from 'react';
import PressableScale from '../components/motion/PressableScale';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Platform,
  Dimensions,
  Animated,
  Easing,
} from 'react-native';
import { isMobile, isTablet, SCREEN_WIDTH } from '../utils/responsive';
import { LinearGradient } from 'expo-linear-gradient';
import LoadingSpinner from '../components/LoadingSpinner';
import { Image } from 'react-native';
import { BlurView } from 'expo-blur';
import Svg, { Path, Defs, LinearGradient as SvgLinearGradient, Stop, Mask } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from '../components/NavigationHeader';
import { ThemeGradientOverlay, ThemeGlassBackground } from '../components/ThemeGradients';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import SearchInput from '../components/SearchInput';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { theme, useBreakpoint } from '../constants/theme';
import { auth, db } from '../firebaseConfig';
import { collection, query as firestoreQuery, where, getDocs, orderBy, limit, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

const getViewport = () => Dimensions.get('window');


// Screenshot Icon Component
export const ScreenshotIcon = ({ size = 23 }) => (
  <View style={{ width: size, height: size, borderRadius: 4, justifyContent: 'center', alignItems: 'center' }}>
    <Ionicons name="images-outline" size={size} color="white" />
  </View>
);

// Small Logo Icon Component
export const SmallLogoIcon = ({ size = 19 }) => (
  <Image source={require('../assets/icon.png')} style={{ width: size, height: size, borderRadius: 6 }} resizeMode="contain" />
);



// Add Leg Button Component
const AddLegButton = ({ onPress }) => {
  const glowT = useRef(new Animated.Value(0)).current;
  const { isGradientEnabled, enableGradient, disableGradient } = useTheme();
  const handleIn = () => {
    enableGradient('searchButton');
    // Stop any running animations and get current value
    glowT.stopAnimation();
    
    // Calculate remaining duration based on current value
    const currentGlow = glowT._value || 0;
    const remainingDuration = 1200 * (1 - currentGlow);
    
    Animated.timing(glowT, { toValue: 1, duration: remainingDuration, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start();
  };
  const handleOut = () => {
    // Stop any running animations and get current value
    glowT.stopAnimation();
    
    // Calculate remaining duration based on current value
    const currentGlow = glowT._value || 0;
    const remainingDuration = 1200 * currentGlow;
    
    Animated.timing(glowT, { toValue: 0, duration: remainingDuration, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start(() => {
      // Small delay to ensure animation is completely finished
      setTimeout(() => {
        disableGradient('searchButton');
      }, 50);
    });
  };
  return (
    <PressableScale style={styles.buttonContainer} onPress={onPress} {...(Platform.OS === 'web' ? { onMouseEnter: handleIn, onMouseLeave: handleOut } : {})}>
      <BlurView intensity={60} tint="dark" style={styles.button}>
        <ThemeGradientOverlay 
          opacity={glowT} 
          borderRadius={styles.button.borderRadius || 12} 
          enabled={isGradientEnabled('searchButton')} 
        />
        <Text style={styles.buttonPlus}>→</Text>
        <Text style={styles.buttonText}>Search</Text>
      </BlurView>
    </PressableScale>
  );
};

// Main Home Screen Component
export default function HomeScreen() {
  // Navigation context
  const { navigateTo } = useNavigation();
  const searchDraftRef = useRef('');
  const [searchFieldResetKey, setSearchFieldResetKey] = useState(0);
  const { user, userProfile } = useAuth();
  const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
  const [savedQueries, setSavedQueries] = useState([]);
  const [showSavedQueries, setShowSavedQueries] = useState(false);
  const [isSearching, setIsSearching] = useState(false); // Loading state for search
  const [searchQuery, setSearchQuery] = useState(''); // Current query being searched
  
  // Check if background query mode is enabled
  const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;
  
  // Debug: Log background mode setting (can remove after debugging)
  useEffect(() => {
    if (userProfile) {
      console.log('[HomeScreen] backgroundQueryMode:', backgroundQueryMode, 'preferences:', userProfile?.preferences);
    }
  }, [userProfile, backgroundQueryMode]);
  
  // Ticker context for header height calculation
  const { isTickerEnabled, totalHeaderHeight } = useTicker();
  
  // Animation values
  const pageOpacity = useRef(new Animated.Value(0)).current;
  const pageTranslateY = useRef(new Animated.Value(20)).current;
  const [isExiting, setIsExiting] = useState(false);
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  const bp = useBreakpoint();

  // Title font-size — measured from actual container so text never overflows.
  // container must have overflow:'hidden' so it can't expand with the text,
  // otherwise onLayout returns the already-expanded width.
  // CHAR_RATIO = 1.15: ClimateCrisis uppercase is very wide; 15% safety margin.
  const [titleContainerWidth, setTitleContainerWidth] = useState(0);
  const _titleIsXs = bp.isXs; // xs <480px: two-line "BET / OPTIMIZED"
  const _titleChars = _titleIsXs ? 9 : 11; // widest part: OPTIMIZED=9, BETOPTIMIZED=11
  const _CHAR_RATIO = 1.15;
  const _maxTitleSize = 112;
  // Fallback before first layout — small enough to never overflow at any size.
  const _titleFallback = responsive(26, 38, 60, 88);
  const computedTitleFontSize = titleContainerWidth > 0
    ? Math.min(Math.floor(titleContainerWidth / (_titleChars * _CHAR_RATIO)), _maxTitleSize)
    : _titleFallback;

  // Fetch saved queries
  useEffect(() => {
    const fetchSavedQueries = async () => {
      if (!user?.uid) {
        setSavedQueries([]);
        return;
      }
      
      try {
        const q = firestoreQuery(
          collection(db, 'saved_queries'),
          where('uid', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const snapshot = await getDocs(q);
        const queries = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSavedQueries(queries);
      } catch (error) {
        console.error('[HomeScreen] Error fetching saved queries:', error);
      }
    };
    
    fetchSavedQueries();
  }, [user?.uid]);

  // Handle search start (show loading, then navigate) - only for foreground queries
  const handleSearchStart = (query, isBackground = false) => {
    console.log('[HomeScreen] Search starting for:', query, 'background:', isBackground);
    setSearchQuery(query);
    // Only show loading overlay for foreground queries
    if (!isBackground) {
    setIsSearching(true);
    }
    // Navigation will happen after the timeout in SearchInput
  };

  // Page load animation
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Title sizing is now handled entirely by CSS clamp() — no JS resize needed.
    }
    Animated.parallel([
      Animated.timing(pageOpacity, {
        toValue: 1,
        duration: 1000,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false,
      }),
      Animated.timing(pageTranslateY, {
        toValue: 0,
        duration: 1000,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false,
      }),
    ]).start();

    Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 2800,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 2800,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, []);

  // Exit animation function
  const exitAnimation = (callback) => {
    setIsExiting(true);
    Animated.parallel([
      Animated.timing(pageOpacity, {
        toValue: 0,
        duration: 600,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: false,
      }),
      Animated.timing(pageTranslateY, {
        toValue: 20,
        duration: 600,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: false,
      }),
    ]).start(() => {
      if (callback) callback();
    });
  };

  // Register exit animation with navigation context
  useEffect(() => {
    // Store the exit animation function globally so navigation can access it
    if (Platform.OS === 'web') {
      window.homeExitAnimation = exitAnimation;
    }

    return () => {
      // Clean up on unmount
      if (Platform.OS === 'web') {
        delete window.homeExitAnimation;
      }
    };
  }, []);
  return (
    <View style={[styles.container, { backgroundColor: theme.colors.backgroundAlt }]}>
      {/* Background */}
      <View style={[styles.background, { backgroundColor: theme.colors.backgroundAlt }]} />

      {/* Faint blue glow at bottom — shimmers via opacity loop */}
      <Animated.View style={[styles.blueGlow, {
        opacity: shimmerAnim.interpolate({ inputRange: [0, 1], outputRange: [0.4, 1.0] })
      }]}>
        {Platform.OS === 'web' ? (
          <View style={[StyleSheet.absoluteFill, {
            background: 'radial-gradient(ellipse 80% 60% at 50% 100%, rgba(30,80,220,0.18) 0%, transparent 70%)'
          }]} />
        ) : (
          <LinearGradient
            colors={['transparent', 'rgba(30,80,220,0.14)']}
            style={StyleSheet.absoluteFill}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
          />
        )}
      </Animated.View>
      
      {/* Splash Screen Container */}
      <View style={[styles.splashContainer, {
        paddingHorizontal: Platform.OS === 'web' ? theme.layout.pagePaddingX(bp.width) : 24,
        // Account for fixed header (navbar + ticker if enabled)
        paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + theme.layout.pagePaddingY(bp.width)) : 16,
        paddingBottom: Platform.OS === 'web' ? theme.layout.pagePaddingY(bp.width) : 24,
        gap: theme.spacing(12)
      }]}>
        
        {/* Navigation Bar */}
        <NavigationHeader />
        
        {/* Main Content */}
        <Animated.View 
          style={[
            styles.mainContent,
            {
              opacity: pageOpacity,
              transform: [
                { translateY: pageTranslateY }
              ]
            },
            (Platform.OS === 'web' && (bp.isLg || bp.isXl)) ? { overflow: 'visible' } : null
          ]} 
        >
          <View style={[
            styles.contentContainer,
            (Platform.OS === 'web' && (bp.isLg || bp.isXl)) ? { overflow: 'visible' } : null
          ]}>
            {/* Title + tagline */}
                        <View style={styles.brandBlock}>
                          <View
                            style={[styles.titleContainer, { width: '100%', overflow: 'hidden' }]}
                            onLayout={(e) => setTitleContainerWidth(e.nativeEvent.layout.width)}
                          >
                            <Text
                              numberOfLines={_titleIsXs ? 2 : 1}
                              style={[
                                styles.title,
                                {
                                  fontSize: computedTitleFontSize,
                                  lineHeight: Math.round(computedTitleFontSize * 1.12),
                                }
                              ]}
                            >{_titleIsXs ? 'BET\nOPTIMIZED' : 'BETOPTIMIZED'}</Text>
                          </View>
                          <Text nativeID="home-brand-tagline" style={styles.subtitle}>
                            EVERY BOOK · EVERY MARKET · ONE EDGE
                          </Text>
                        </View>
            {/* Search Input */}
            <SearchInput 
              onNavigate={navigateTo} 
              fieldResetKey={searchFieldResetKey}
              onValueChange={(t) => { searchDraftRef.current = t; }}
              onSearchStart={(q) => handleSearchStart(q, backgroundQueryMode && canAddBackgroundQuery)}
              onExecuteSearch={async (q) => {
                // Check if background query mode is enabled
                if (backgroundQueryMode && canAddBackgroundQuery) {
                  // Just add to background queue - no loading overlay.
                  // Do NOT hardcode league/sport defaults — backend classifyQuery()
                  // detects the league from the prompt (e.g. "nhl fd sgps" → nhl).
                  // Hardcoding 'nba' here caused cross-league contamination in
                  // the SGP cache shortcut path.
                  await addBackgroundQuery(q, {});
                  searchDraftRef.current = '';
                  setSearchFieldResetKey((k) => k + 1);
                } else {
                  // Navigate immediately — SgpFlow enters loading state on mount (no empty flash).
                  navigateTo('SgpFlow', { payload: { query: q } });
                }
              }}
            />
            {/* Add Leg Button */}
            <AddLegButton onPress={async () => {
              const q = (searchDraftRef.current || '').trim();
              if (q) {
                // Check if background query mode is enabled
                if (backgroundQueryMode && canAddBackgroundQuery) {
                  // Same reasoning as above — no hardcoded league/sport.
                  await addBackgroundQuery(q, {});
                  searchDraftRef.current = '';
                  setSearchFieldResetKey((k) => k + 1);
                } else {
                try { if (Platform.OS === 'web') window.sessionStorage.setItem('sgp:lastQuery', q); } catch {}
                navigateTo('SgpFlow', { payload: { query: q } });
                }
              }
            }} />
            
            {/* Saved Queries Section (subtle) */}
            {savedQueries.length > 0 && (
              <View style={{alignItems:'center', width: '100%', marginTop: 24}}>
                <PressableScale
                  onPress={() => setShowSavedQueries(!showSavedQueries)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    backgroundColor: 'rgba(255,255,255,0.03)',
                  }}
                >
                  <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                    {savedQueries.length} saved quer{savedQueries.length !== 1 ? 'ies' : 'y'}
                  </Text>
                  <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                    {showSavedQueries ? '▼' : '▶'}
                  </Text>
                </PressableScale>
                
                {showSavedQueries && (
                  <View style={{width: '100%', maxWidth: 600, marginTop: 12, gap: 8}}>
                    {savedQueries.map((sq) => (
                      <PressableScale
                        key={sq.id}
                        onPress={async () => {
                          if (backgroundQueryMode && canAddBackgroundQuery) {
                            // Let backend classifyQuery detect league/sport from the prompt.
                            await addBackgroundQuery(sq.query, {});
                          } else {
                            navigateTo('SgpFlow', { payload: { query: sq.query } });
                          }
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingVertical: 10,
                          paddingHorizontal: 14,
                          borderRadius: 8,
                          backgroundColor: 'rgba(255,255,255,0.04)',
                          borderWidth: 1,
                          borderColor: 'rgba(255,255,255,0.08)',
                        }}
                      >
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: '600' }}>
                            {sq.query}
                          </Text>
                          {sq.teamName && (
                            <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                              {sq.teamName} · {sq.fixtureCount} game{sq.fixtureCount !== 1 ? 's' : ''}
                            </Text>
                          )}
                        </View>
                        <PressableScale
                          onPress={async (e) => {
                            e.stopPropagation();
                            try {
                              await deleteDoc(doc(db, 'saved_queries', sq.id));
                              setSavedQueries(prev => prev.filter(q => q.id !== sq.id));
                            } catch (error) {
                              console.error('Error deleting query:', error);
                            }
                          }}
                          style={{
                            padding: 6,
                            borderRadius: 6,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                          }}
                        >
                          <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>✕</Text>
                        </PressableScale>
                      </PressableScale>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>
        </Animated.View>
        
      </View>
      
      {/* Loading Overlay - Shows during search initialization */}
      {isSearching && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingContent}>
            <LoadingSpinner size={32} color="#a259ff" />
            <Text style={styles.loadingText}>Searching for {searchQuery}...</Text>
            <Text style={styles.loadingSubtext}>Checking cache...</Text>
          </View>
        </View>
      )}
    </View>
  );
}

const isVerySmall = SCREEN_WIDTH < 500; // Small phones (up to ~iPhone 14 Pro Max size)

// Responsive helper function with extra small breakpoint
const responsive = (verySmall, mobile, tablet, desktop) => {
  if (isVerySmall) return verySmall;
  if (isMobile) return mobile;
  if (isTablet) return tablet;
  return desktop;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
    height: Platform.OS === 'web' ? '100vh' : '100%',
    maxHeight: Platform.OS === 'web' ? '100vh' : '100%',
    overflow: 'hidden',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000000',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  blueGlow: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
    pointerEvents: 'none',
  },
  splashContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
 
  // Main Content Styles
  mainContent: {
    flex: 1,
    width: '100%',
    borderRadius: responsive(12, 12, 16, 20),
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderBottomWidth: 2,
    borderTopColor: 'rgba(255,255,255,0.25)',
    borderLeftColor: 'rgba(255,255,255,0.18)',
    borderRightColor: 'rgba(255,255,255,0.12)',
    borderBottomColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
    paddingVertical: responsive(20, 24, 36, 48),
    paddingHorizontal: responsive(8, 12, 16, 0),
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    width: '100%',
    maxWidth: 1280,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: responsive(4, 8, 12, 16),
    gap: responsive(12, 16, 20, 24),
    overflow: 'hidden',
  },
  
  // Title Styles
  brandBlock: {
    width: '100%',
    alignItems: 'center',
    overflow: 'visible',
    gap: responsive(8, 10, 12, 14),
  },
  titleContainer: {
    alignItems: 'center',
    gap: responsive(4, 6, 8, 10),
  },
    title: {
      fontFamily: 'ClimateCrisis',
      // Fallback sizes — intentionally conservative so text never overflows
      // before onLayout fires and sets the correct size.
      fontSize: responsive(26, 38, 60, 88),
      fontWeight: '400',
      lineHeight: responsive(30, 43, 68, 98),
    textTransform: 'uppercase',
    textAlign: 'center',
    color: 'rgba(255,255,255,0.85)',
    maxWidth: '100%',
    marginLeft: responsive(0, 0, 8, 12),
    marginRight: responsive(0, 0, 8, 12),
    paddingHorizontal: responsive(4, 4, 0, 0),
    letterSpacing: responsive(-0.5, -0.5, -1.5, -1.5),
  },
  subtitle: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: responsive(11, 12, 14, 16),
    fontWeight: '600',
    letterSpacing: responsive(1.4, 2, 2.6, 3.2),
    lineHeight: responsive(16, 18, 20, 22),
    textAlign: 'center',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.62)',
    paddingHorizontal: responsive(8, 12, 16, 24),
    marginTop: responsive(2, 4, 4, 6),
    maxWidth: '100%',
  },
  
  // Search Input Styles
  searchContainer: {
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 1200 : '100%',
    paddingVertical: responsive(12, 16, 24, 32),
    alignItems: 'center',
    gap: responsive(8, 8, 10, 12),
    overflow: 'hidden',
  },
  searchAnimatedWrap: {
    width: '100%',
    overflow: 'hidden',
  },
  searchInput: {
    width: '100%',
    height: responsive(48, 52, 56, 60),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: responsive(12, 14, 17, 20),
    paddingVertical: responsive(8, 10, 12, 14),
    borderRadius: responsive(10, 12, 14, 16),
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.25)',
    borderLeftColor: 'rgba(255,255,255,0.18)',
    borderRightColor: 'rgba(0,0,0,0.35)',
    borderBottomColor: 'rgba(0,0,0,0.45)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    shadowColor: 'rgba(0, 0, 0, 0.25)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 4,
  },
  // border radius is animated; keep for native parity if needed
  searchFocused: { },
  searchText: {
    flex: 1,
    fontFamily: 'Slate',
    fontSize: responsive(13, 14, 16, Platform.OS === 'web' ? 18 : 15),
    fontWeight: '600',
    lineHeight: responsive(16, 18, 20, Platform.OS === 'web' ? 22 : 20),
    color: 'rgba(255, 255, 255, 0.9)',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  iconsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: responsive(10, 12, 16, 20),
  },
  
  // Button Styles
  buttonContainer: { marginTop: responsive(8, 12, 18, 24) },
  button: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: responsive(8, 10, 12, 15),
    paddingVertical: responsive(12, 14, 16, 18),
    paddingHorizontal: responsive(20, 24, 30, 36),
    borderRadius: responsive(10, 10, 11, 12),
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.25)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    shadowColor: 'rgba(0, 0, 0, 0.25)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 4,
  },
  buttonPlus: {
    fontFamily: 'Outfit',
    fontSize: responsive(16, 18, 20, 22),
    fontWeight: '700',
    lineHeight: responsive(18, 20, 22, 24),
    color: '#FFF',
  },
  buttonText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: responsive(14, 15, 16, 18),
    fontWeight: '800',
    lineHeight: responsive(16, 18, 20, 22),
    color: 'rgba(255,255,255,0.95)',
  },
  
  // Loading Overlay Styles
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  loadingContent: {
    alignItems: 'center',
    gap: 16,
    padding: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(162, 89, 255, 0.3)',
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 8,
  },
  loadingSubtext: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.6)',
  },
});
